import React, { useEffect, useState } from "react"
import Button from "@mui/material/Button"
import LinearProgress from "@mui/material/LinearProgress"
import Pagination from "@mui/material/Pagination"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import { useSnackbar } from "notistack"
import { UseConfirmDialog } from "components/customHooks"
import { getReportedComments, ignoreCommentReport, removeReportedComment, banAuthorByComment } from "services/community"
import { buildCommunityPostUrl } from "utils/platformUrl"
import { StyledCommunityReports } from "./Community.style"

const ReportedComments = () => {
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const { enqueueSnackbar } = useSnackbar()

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Confirm moderation action",
    description: "Are you sure you want to continue?",
    agreeText: "Proceed",
    disagreeText: "Cancel",
  })

  const load = async (p = 1) => {
    setLoading(true)
    setError(null)
    try {
      const res = await getReportedComments(p, pageSize)
      setRows(Array.isArray(res?.data) ? res.data : [])
      setTotal(Number(res?.total) || 0)
      setPage(Number(res?.page) || p)
    } catch (e) {
      setError("Failed to load reported comments")
    } finally {
      setLoading(false)
    }
  }

  const performAction = async (action, successMessage, errorMessage) => {
    setActionLoading(true)
    try {
      await action()
      enqueueSnackbar(successMessage, { variant: "success" })
      await load(page)
    } catch (e) {
      enqueueSnackbar(errorMessage, { variant: "error" })
    } finally {
      setActionLoading(false)
    }
  }

  useEffect(() => {
    load(1)
  }, [])

  const onIgnore = async (reportId) => {
    if (!reportId) return
    await performAction(
      () => ignoreCommentReport(reportId),
      "Comment report ignored",
      "Failed to ignore comment report"
    )
  }

  const onRemove = async (commentId) => {
    if (!commentId) return
    await performAction(
      () => removeReportedComment(commentId),
      "Reported comment removed",
      "Failed to remove reported comment"
    )
  }

  const onBan = async (commentId) => {
    if (!commentId) return
    await performAction(
      () => banAuthorByComment(commentId),
      "Comment author banned",
      "Failed to ban comment author"
    )
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <StyledCommunityReports>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Reported Comments</h1>
          <p className="page-subtitle">
            Review flagged comments and apply moderation actions quickly.
          </p>
        </div>
        <div className="actions"></div>
      </PageHeading>
      <div className="page-surface">
        <div className="status-row">
          <div className="status-info">Total reports: {total}</div>
          {error && <div className="status-error">{error}</div>}
        </div>
        <div className="table-shell">
          <TableContainer component={Paper}>
            {loading && <LinearProgress />}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reported At</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell className="empty-row" colSpan={6}>
                      No reported comments found.
                    </TableCell>
                  </TableRow>
                )}
                {(rows || []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                    <TableCell className="content-cell">
                      <div className="muted">on Post #{r.comment?.postId || "-"}</div>
                      <div className="content-subtext">{r.comment?.content || "-"}</div>
                      {r.comment?.postId && (
                        <a
                          className="report-link"
                          href={buildCommunityPostUrl(r.comment.postId)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Post
                          <OpenInNewRoundedIcon fontSize="inherit" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.comment?.author ? (
                        <div>
                          <div>{r.comment.author.name}</div>
                          <div className="muted">{r.comment.author.username}</div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{r.reporter ? r.reporter.name : "-"}</TableCell>
                    <TableCell>
                      <div className="reason-main">{r.reason || "-"}</div>
                      {r.details && <div className="muted">{r.details}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="action-group">
                        <Button
                          className="action-btn remove"
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            openConfirmDialog(
                              () => onRemove(r.comment?.id),
                              "Remove this comment from the community thread?"
                            )
                          }}
                          disabled={actionLoading || !r.comment?.id}
                        >
                          Remove Comment
                        </Button>
                        <Button
                          className="action-btn ban"
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            openConfirmDialog(
                              () => onBan(r.comment?.id),
                              "Ban this comment author from the community?"
                            )
                          }}
                          disabled={actionLoading || !r.comment?.id}
                        >
                          Ban User
                        </Button>
                        <Button
                          className="action-btn ignore"
                          variant="outlined"
                          size="small"
                          onClick={() => onIgnore(r.id)}
                          disabled={actionLoading}
                        >
                          Ignore
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        {totalPages > 1 && (
          <div className="pagination-wrap">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => {
                load(value)
              }}
              color="primary"
              shape="rounded"
              size="small"
            />
          </div>
        )}
      </div>
      {ConfirmDialog}
    </StyledCommunityReports>
  )
}

export default ReportedComments


